import { DataItem } from '../../../@types/DataItem';

export type NetworkPriceStatus ='AUTO' | 'MANUAL' | 'ERP' | 'EDITADO' | 'OTIMIZADO';

export interface NetworkPriceCluster {
  clusterId: string;
  clusterName: string;
  clusterStoresCount: number | null;
  productsToBePricedId: string | null;
  productId: string | null;
  storesCount: number | null;
  price: number | null;
  retailPrice: number | null;
  suggestedPrice: number | null;
  editedPrice: number | null;
  optimizedPrice: number | null;
  priceStatus: NetworkPriceStatus | null;
  priceOrigin: string | null;
  priceVariation: number | null;
  brokenLimit: boolean;
  brokenLimitTypes: string[];
  // Price-list bounds the price manager paints red against; not sent by the back-end yet.
  minPriceList?: number | null;
  maxPriceList?: number | null;
  // CPI and margin bounds as fractions (0.28 = 28%), like the price manager.
  minCpi?: number | null;
  maxCpi?: number | null;
  minMargin?: number | null;
  maxMargin?: number | null;
  cpi: number | null;
  newMargin: number | null;
  originalMargin: number | null;
  pmz: number | null;
  competitorsPrice: number | null;
  salesTaxPercentage?: number | null;
  expensesPercent?: number | null;
  cost?: number | null;
}

export interface NetworkPriceLine {
  productId: string;
  productFamilyId: string | null;
  description: string;
  isProductMaster: boolean | null;
  itemsCountFamily: number | null;
  averagePrice: number | null;
  storesCount: number | null;
  clusters: NetworkPriceCluster[];
}

export interface NetworkPricesPage {
  content: NetworkPriceLine[];
  totalElements: number;
  number: number;
  size: number;
}

// Every category level the filter bar and the back-end accept; types and maps of the filters derive from this list.
export const NETWORK_CATEGORY_KEYS = [
  'categoryLevel1',
  'categoryLevel2',
  'categoryLevel3',
  'categoryLevel4',
  'categoryLevel5',
  'categoryLevel6',
  'categoryLevel7',
] as const;

export type NetworkCategoryKey = (typeof NETWORK_CATEGORY_KEYS)[number];

export type NetworkFilterKey = 'productIds' | 'productFamilyIds' | 'productBrand' | 'supplier' | NetworkCategoryKey;

export type NetworkFilterValues = Partial<Record<NetworkFilterKey, string[]>>;

// Selections of the other chips, named as the option endpoints expect them. Brand and supplier never cascade,
// as in the price manager: their endpoints take only the search.
export type NetworkOptionsCascade = Partial<Record<'productId' | 'productFamilyId' | NetworkCategoryKey, string[]>>;

// A pasted list of codes loads many more options than a typed search.
export type NetworkOptionsLookup = { isPaste?: boolean };

export type NetworkFilterOptions = {
  data: DataItem[];
  value: string[];
  // The CheckPicker keeps its own input text in sync with this, so a reset search also clears the box.
  search: string;
  isLoading: boolean;
  onSearch: (query: string) => void;
  onOpen: () => void;
  onClose: () => void;
  onChange: (values: string[]) => void;
  onClean: () => void;
  onConfirmPasteValues: (text: string) => Promise<void>;
};

export interface NetworkPricesPagination {
  page: number;
  size: number;
}

export interface ApplyNetworkPriceItem {
  productsToBePricedId: string;
  price: number;
}

// One entry per cluster saved by the network average price edit.
export interface EditedNetworkClusterPrice {
  clusterId: string;
  productsToBePricedId: string;
  price: number;
  newMargin: number | null;
  newCompetitivenessPrice: number | null;
  priceStatus: NetworkPriceStatus;
}

export interface ClusterMetrics {
  price: number | null;
  priceVariation: number | null;
  cpi: number | null;
  cpiVariation: number | null;
  margin: number | null;
  marginVariation: number | null;
}

export interface ClusterLimitBreaks {
  priceList: boolean;
  cpi: boolean;
  margin: boolean;
}

export type LineKey = string;

// Identifies one decision of a line in one cluster (the editable cell).
export type ProductsToBePricedId = string;

export type DraftPrices = Record<ProductsToBePricedId, number | null>;

export type NetworkDraftPrices = Record<LineKey, number | null>;

export type NetworkPriceRow = NetworkPriceLine & { lineKey: LineKey };

export type ClusterColumn = Pick<NetworkPriceCluster, 'clusterId' | 'clusterName' | 'clusterStoresCount'>;

export type ClusterCellSkin = 'gray' | 'blue' | 'red';

export interface LastIntegration {
  lastExecutionDate: string;
  engineStatus: 'NOT_UPDATED' | 'UPDATED' | 'UPDATING';
}
